import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useErrorLog, useErrorLogCodes, useProfilesList, type ErrorLogRow } from "@/lib/queries";
import { useClearErrorLog } from "@/lib/mutations";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Bug, ChevronLeft, ChevronRight, Funnel, Trash2, X } from "lucide-react";
import { fmtDateTime, dateIST } from "@emr/shared/india";
import { PermissionGate } from "@/components/PermissionGate";

function statusBadge(status: number): "default" | "secondary" | "destructive" | "outline" {
  if (status >= 500) return "destructive";
  if (status >= 400) return "secondary";
  return "outline";
}

export default function ErrorLogPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialUser = searchParams.get("user") ?? "";

  const [user, setUser] = useState(initialUser);
  const [code, setCode] = useState("");
  const [status, setStatus] = useState("");
  // Lazy initializers keep clock reads out of render (purity).
  const [from, setFrom] = useState(() => dateIST(Date.now() - 7 * 24 * 3600 * 1000));
  const [to, setTo] = useState(() => dateIST());
  const [page, setPage] = useState(1);
  const limit = 50;

  useEffect(() => {
    const u = searchParams.get("user") ?? "";
    if (u && u !== user) setUser(u);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    setPage(1);
    const next = new URLSearchParams();
    if (user) next.set("user", user);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const { data, isLoading, isFetching } = useErrorLog({
    code: code || undefined,
    status: status ? parseInt(status, 10) : undefined,
    user: user || undefined,
    from: from || undefined,
    to: to || undefined,
    page,
    limit,
  });
  const { data: codesData } = useErrorLogCodes();
  const { data: profiles } = useProfilesList();
  const clearErrorLog = useClearErrorLog();

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  const clearFilters = () => {
    setUser("");
    setCode("");
    setStatus("");
    setFrom(dateIST(Date.now() - 7 * 24 * 3600 * 1000));
    setTo(dateIST());
    setPage(1);
  };

  // "Clear all logs" — destructive, so confirm first (same pattern as the
  // admin users page's delete). The server audits the purge with the row
  // count, so the action is traceable in the audit log.
  const clearAllLogs = () => {
    if (total === 0) return;
    if (!confirm(`Clear all ${total} error log ${total === 1 ? "entry" : "entries"}? This cannot be undone.`)) return;
    clearErrorLog.mutate(undefined, {
      onSuccess: () => {
        setPage(1);
        clearFilters();
      },
    });
  };

  return (
    <PermissionGate perm="errorlog.view">
      <div className="mx-auto max-w-7xl p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="font-serif text-2xl font-bold text-maroon-700 dark:text-amber-500 flex items-center gap-2">
            <Bug size={20} /> Error Log
          </h1>
          <div className="flex items-center gap-3">
            <div className="text-xs text-muted">
              {total} entr{total === 1 ? "y" : "ies"} · page {page} of {totalPages} · retained 3 months
            </div>
            <Button
              variant="destructive"
              size="sm"
              onClick={clearAllLogs}
              disabled={clearErrorLog.isPending || total === 0}
            >
              <Trash2 size={14} className="mr-1" />
              {clearErrorLog.isPending ? "Clearing…" : "Clear all logs"}
            </Button>
          </div>
        </div>

        <Card>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-6 items-end">
              <div>
                <Label className="text-xs">User</Label>
                <Select value={user || "all"} onValueChange={(v) => setUser(v === "all" ? "" : v)}>
                  <SelectTrigger><SelectValue placeholder="All users" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All users</SelectItem>
                    {(profiles ?? []).map((p) => (
                      <SelectItem key={p.profileId} value={p.profileId}>{p.fullName} ({p.role})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Code</Label>
                <Select value={code || "all"} onValueChange={(v) => setCode(v === "all" ? "" : v)}>
                  <SelectTrigger><SelectValue placeholder="All codes" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All codes</SelectItem>
                    {(codesData?.codes ?? []).map((c) => (
                      <SelectItem key={c.code} value={c.code}>{c.code} ({c.count})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Status</Label>
                <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
                  <SelectTrigger><SelectValue placeholder="All statuses" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    {[400, 401, 403, 404, 409, 422, 429, 500, 502, 503].map((s) => (
                      <SelectItem key={s} value={String(s)}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">From</Label>
                <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
              </div>
              <div>
                <Label className="text-xs">To</Label>
                <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
              </div>
              <div>
                <Button variant="outline" onClick={clearFilters} className="w-full">
                  <X size={14} className="mr-1" /> Reset filters
                </Button>
              </div>
            </div>
            {isFetching && <div className="mt-2 text-xs text-muted">Refreshing…</div>}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Timestamp</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead>Method + Path</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Message</TableHead>
                  <TableHead>IP</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow><TableCell colSpan={7}><Skeleton className="h-10" /></TableCell></TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-sm text-muted py-10">
                      <Funnel size={32} className="mx-auto mb-2" />
                      No error entries match these filters.
                    </TableCell>
                  </TableRow>
                ) : rows.map((r: ErrorLogRow) => (
                  <TableRow key={r.id}>
                    <TableCell className="text-xs whitespace-nowrap">{fmtDateTime(r.createdAt)}</TableCell>
                    <TableCell>
                      <Badge variant={statusBadge(r.status)} className="text-[10px] font-mono">{r.status}</Badge>
                    </TableCell>
                    <TableCell className="text-xs font-mono">{r.code}</TableCell>
                    <TableCell className="text-xs">
                      <span className="font-mono mr-1">{r.method}</span>
                      <span className="font-mono text-muted">{r.path}</span>
                    </TableCell>
                    <TableCell className="text-sm">{r.userName ?? "—"}</TableCell>
                    <TableCell className="text-xs text-muted max-w-64 truncate" title={r.message ?? undefined}>
                      {r.message ?? "—"}
                    </TableCell>
                    <TableCell className="text-xs text-muted font-mono">{r.ip ?? "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1}>
            <ChevronLeft size={14} className="mr-1" /> Prev
          </Button>
          <Button variant="outline" onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page >= totalPages}>
            Next <ChevronRight size={14} className="ml-1" />
          </Button>
        </div>
      </div>
    </PermissionGate>
  );
}
